import { useMutation } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { Switch } from '@/components/elements/switch'
import { FileUploader } from '@/components/FileUploader'
import { Modal } from '@/components/Modal'
import { RichTextEditor } from '@/components/RichTextEditor'
import { addFaq, updateFaq } from '@/services/faq.service'
import { toast } from '@/utils/helpers'

const VIDEO_SOURCES = [
  { id: 'none', label: 'No video' },
  { id: 'url', label: 'Link to a video' },
  { id: 'upload', label: 'Upload a video' },
]

/**
 * Create / edit an FAQ entry (RSCX-353): question, rich text answer, and an
 * optional video attached as either an external link or an upload.
 */
const AddEditFaqModal = ({ editFaq, resetAndClose }) => {
  const isEdit = Boolean(editFaq)

  const initialSource = (() => {
    if (editFaq?.videoUrl) return 'url'
    if (editFaq?.videoPath) return 'upload'
    return 'none'
  })()

  const form = useForm({
    defaultValues: {
      question: editFaq?.question ?? '',
      answer: editFaq?.answer ?? '',
      videoSource: initialSource,
      videoUrl: editFaq?.videoUrl ?? '',
      videoPath: editFaq?.videoPath ?? '',
      isVisible: editFaq?.isVisible ?? true,
    },
  })

  const [isUploading, setIsUploading] = useState(false)

  const videoSource = form.watch('videoSource')

  const { isPending, mutate: saveFaq } = useMutation({
    mutationFn: (payload) =>
      isEdit ? updateFaq({ id: editFaq.id, ...payload }) : addFaq(payload),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      toast(isEdit ? `${data.question} updated` : `${data.question} added`, 'success')
      resetAndClose()
    },
  })

  const onSubmit = ({ videoSource: source, videoUrl, videoPath, ...data }) => {
    if (isUploading) return
    // The backend enforces upload/link exclusivity; send only the chosen one.
    saveFaq({
      ...data,
      videoUrl: source === 'url' ? videoUrl.trim() : null,
      videoPath: source === 'upload' ? videoPath : null,
    })
  }

  return (
    <Modal
      size="lg"
      title={isEdit ? 'Edit FAQ' : 'Add FAQ'}
      setOpen={resetAndClose}
      loading={isPending}
      actions={[
        { label: 'Cancel', onClick: resetAndClose, type: 'cancel' },
        {
          label: isUploading ? 'Uploading…' : 'Save FAQ',
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
          disabled: isUploading,
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="question"
              rules={{ required: 'A question is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Question</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. How do I assign a course?" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="answer"
              rules={{
                validate: (value) => (value && value !== '<p></p>') || 'An answer is required',
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Answer</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      className="w-full"
                      editorContentClassName="p-5"
                      output="html"
                      label="Answer"
                      placeholder="Write the answer…"
                      editable
                      editorClassName="focus:outline-hidden"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isVisible"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-3">
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div>
                    <FormLabel>Visible to users</FormLabel>
                    <p className="text-muted-foreground text-xs">
                      Turn off to keep this FAQ as a draft only admins can see.
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="videoSource"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Video</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-56">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VIDEO_SOURCES.map((source) => (
                        <SelectItem key={source.id} value={source.id}>
                          {source.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            {videoSource === 'url' && (
              <FormField
                control={form.control}
                name="videoUrl"
                rules={{
                  required: 'Enter a video URL',
                  pattern: { value: /^https?:\/\/.+/i, message: 'Enter a valid URL' },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Video URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://www.youtube.com/watch?v=…" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {videoSource === 'upload' && (
              <FormItem>
                <FormLabel>Video file</FormLabel>
                <FileUploader
                  acceptedFileTypes={['video/*']}
                  filePrefix="faqs/"
                  type="aws"
                  maxFiles={1}
                  handleUploadToServer={async (file) => {
                    form.setValue('videoPath', file.fileUploadName, { shouldDirty: true })
                  }}
                  onaddfilestart={() => setIsUploading(true)}
                  onprocessfile={() => setIsUploading(false)}
                  onprocessfileabort={() => setIsUploading(false)}
                  onerror={() => setIsUploading(false)}
                />
                {form.watch('videoPath') && (
                  <p className="text-muted-foreground text-xs">Video attached.</p>
                )}
              </FormItem>
            )}
          </form>
        </Form>
      }
    />
  )
}

AddEditFaqModal.propTypes = {
  editFaq: PropTypes.object,
  resetAndClose: PropTypes.func.isRequired,
}

export default AddEditFaqModal
